import { sortBy } from "lodash";

import timezones from "../i18n/timezones.json";
import { translateWeekday } from "./dates";

export interface StoreTimetableEntry {
  day: number;
  time: string | null;
}

function getLocalizedStoreTimetableEntry(
  store: Queries.CompanySiteDetailDataFragment,
  { time, day }: StoreTimetableEntry
) {
  const timeZone = timezones.find(({ text }) => text === store.timezone);

  if (!timeZone) throw new Error("Unknown timezone");

  const [hours, minutes] = time!.split(":").map((x) => +x);
  let localizedTimeInMinutes =
    minutes + hours * 60 - timeZone.offset * 60 - new Date().getTimezoneOffset();
  let localizedDay = day;

  while (localizedTimeInMinutes < 0) {
    localizedDay = (localizedDay - 1 + 7) % 7;
    localizedTimeInMinutes += 24 * 60;
  }
  while (localizedTimeInMinutes > 24 * 60) {
    localizedDay = (localizedDay + 1 + 7) % 7;
    localizedTimeInMinutes -= 24 * 60;
  }
  const localizedHours = Math.floor(localizedTimeInMinutes / 60);
  const localizedMinutes = localizedTimeInMinutes - localizedHours * 60;
  const localizedTime = [localizedHours, localizedMinutes]
    .map((number) =>
      number.toLocaleString("en", { minimumIntegerDigits: 2, maximumFractionDigits: 0 })
    )
    .join(":");

  return { day: localizedDay, time: localizedTime };
}

interface TimetableEvent {
  timeInMinutes: number;
  day: number;
  time: string | null;
  type: "close" | "open";
}

export class StoreTimetable {
  public store: Queries.CompanySiteDetailDataFragment;
  public openingTimes: StoreTimetableEntry[];
  public closingTimes: StoreTimetableEntry[];

  constructor(store: Queries.CompanySiteDetailDataFragment) {
    this.store = store;

    this.openingTimes = [
      { day: 0, time: store.sundayOpensAt }, // in JS day zero is sunday!
      { day: 1, time: store.mondayOpensAt },
      { day: 2, time: store.tuesdayOpensAt },
      { day: 3, time: store.wednesdayOpensAt },
      { day: 4, time: store.thursdayOpensAt },
      { day: 5, time: store.fridayOpensAt },
      { day: 6, time: store.saturdayOpensAt },
    ]
      .filter(({ time }) => Boolean(time))
      .map((entry) => getLocalizedStoreTimetableEntry(store, entry));

    this.closingTimes = [
      { day: 0, time: store.sundayClosesAt }, // in JS day zero is sunday!
      { day: 1, time: store.mondayClosesAt },
      { day: 2, time: store.tuesdayClosesAt },
      { day: 3, time: store.wednesdayClosesAt },
      { day: 4, time: store.thursdayClosesAt },
      { day: 5, time: store.fridayClosesAt },
      { day: 6, time: store.saturdayClosesAt },
    ]
      .filter(({ time }) => Boolean(time))
      .map((entry) => getLocalizedStoreTimetableEntry(store, entry));
  }

  public getNextTimetableEvent(date = new Date()) {
    const _this = this;

    const closingEvents: Array<TimetableEvent> = _this.closingTimes.map(({ time, day }) => {
      const [hours, minutes] = time!.split(":").map((x) => +x);

      return {
        type: "close",
        timeInMinutes: day * 24 * 60 + hours * 60 + minutes,
        day,
        time,
      };
    });
    const openingEvents: Array<TimetableEvent> = _this.openingTimes.map(({ time, day }) => {
      const [hours, minutes] = time!.split(":").map((x) => +x);

      return {
        type: "open",
        timeInMinutes: day * 24 * 60 + hours * 60 + minutes,
        day,
        time,
      };
    });

    const timetableEvents: Array<TimetableEvent> = sortBy<TimetableEvent>(
      openingEvents.concat(closingEvents),
      "timeInMinutes"
    );

    const currentTimeInMinutes = date.getDay() * 24 * 60 + date.getHours() * 60 + date.getMinutes();

    const nextTimetableEventIndex = timetableEvents.findIndex(
      ({ timeInMinutes }) => timeInMinutes > currentTimeInMinutes
    );

    const nextTimetableEvent =
      nextTimetableEventIndex < 0 ? timetableEvents[0] : timetableEvents[nextTimetableEventIndex];

    const previousTimetableEvent =
      nextTimetableEventIndex < 0
        ? timetableEvents[timetableEvents.length - 1]
        : timetableEvents[
            (nextTimetableEventIndex - 1 + timetableEvents.length) % timetableEvents.length
          ];

    const isOpen = previousTimetableEvent?.type === "open";

    const [dateLanguage] = _this.store.node_locale.split("-");

    let formattedRelativeDay: string | null = null;
    if (nextTimetableEvent) {
      let dayDelta = nextTimetableEvent.day - date.getDay();
      while (dayDelta < 0) dayDelta += 7;
      formattedRelativeDay = nextTimetableEvent
        ? dayDelta < 2 // today or tomorrow
          ? new Intl.RelativeTimeFormat(dateLanguage, {
              numeric: "auto",
            }).format(dayDelta, "day")
          : translateWeekday(nextTimetableEvent.day % 7, dateLanguage)
        : null;
    }

    return {
      isOpen,
      formattedRelativeDay,
      formattedTime: nextTimetableEvent?.time,
    };
  }
}

export function isStoreAlwaysClosed(store: Queries.CompanySiteDetailDataFragment) {
  return ![
    store.mondayOpensAt,
    store.mondayClosesAt,
    store.tuesdayOpensAt,
    store.tuesdayClosesAt,
    store.wednesdayOpensAt,
    store.wednesdayClosesAt,
    store.thursdayOpensAt,
    store.thursdayClosesAt,
    store.fridayOpensAt,
    store.fridayClosesAt,
    store.saturdayOpensAt,
    store.saturdayClosesAt,
    store.sundayOpensAt,
    store.sundayClosesAt,
  ].some(Boolean);
}
